import { Injectable, computed, inject } from '@angular/core';
import { AuthStore } from '../store/auth.store';
import { CompanyContextStore } from '../store/company-context.store';
import { PERMISSIONS } from '../constants/permissions.constants';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly authStore  = inject(AuthStore);
  private readonly companyCtx = inject(CompanyContextStore);

  /** Lista de permisos activos del usuario (señal reactiva). */
  readonly permissions = this.authStore.userPermissions;

  /**
   * `true` cuando el usuario posee el permiso `tenants:view`.
   * Solo el superadministrador recibe este permiso del backend.
   */
  readonly isSuperAdmin = computed(() =>
    this.permissions().includes(PERMISSIONS.TENANTS_VIEW),
  );

  /**
   * `true` cuando el usuario debe ver las vistas de workspace (importaciones,
   * exportaciones, etc.) en lugar de las vistas de administración global.
   *
   * Esto ocurre en dos casos:
   *  - El usuario NO es superadministrador.
   *  - El usuario es superadministrador y está dentro del contexto de una empresa.
   */
  readonly isInWorkspaceMode = computed(() =>
    !this.isSuperAdmin() || this.companyCtx.isInContext(),
  );

  /** Comprueba si el usuario tiene un permiso específico. */
  has(permission: string): boolean {
    return this.permissions().includes(permission);
  }

  /** Comprueba si el usuario tiene al menos uno de los permisos indicados. */
  hasAny(permissions: string[]): boolean {
    const userPerms = this.permissions();
    return permissions.some((p) => userPerms.includes(p));
  }
}
